import React, { useEffect, useState } from "react";
import { Controller, SubmitHandler, useForm } from "react-hook-form";
import ReactSelect from "react-select";
import { AlertCircle } from "lucide-react";
import { useAppDispatch, useAppSelector } from "../../app/hooks";
import {
  fetchProcessDefinitions,
  getEditProcessDefinition,
  getIsProcessDefinitionModalOpen,
  getProcessDefinitions,
  toggleProcessDefinitionModal,
} from "../../features/processDefinitions/processDefinitionsSlice";
import { ProcessDefinitionFormInput, ProcessRateBasis } from "../../types/processDefinition";
import { localizedProcessName } from "../../lib/utils";
import { t } from "../../locales";
import { filterSelectStyles } from "../../static/styles";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "../ui/dialog";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Label } from "../ui/label";
import { Checkbox } from "../ui/checkbox";
import { ProcessModePolicy } from "../../types/processDefinition";

const modalInputClass = (hasError: boolean) =>
  `h-12 text-base border-2 rounded-lg transition-all duration-200 ${
    hasError
      ? "border-red-500 focus:border-red-500 focus:shadow-focus-error"
      : "border-gray-200 focus:border-[#113F67] focus:shadow-focus-brand"
  }`;

const modalSelectStyles: any = {
  ...filterSelectStyles,
  control: (base: any, state: any) => ({
    ...(typeof filterSelectStyles.control === "function"
      ? (filterSelectStyles.control as any)(base, state)
      : base),
    borderRadius: "0.5rem",
    fontSize: "1rem",
  }),
};

const defaultValues: ProcessDefinitionFormInput = {
  slug: "",
  name: "",
  name_gu: "",
  sort_order: "",
  rate_basis: "",
  is_active: true,
  has_order: true,
  has_receive: true,
  mode_policy: "both",
  next_process_definition_ids: [],
  next_process_mandatory: false,
};

type Props = {
  handleSaveProcessDefinition: (data: ProcessDefinitionFormInput) => void;
};

const ProcessDefinitionFormModal = ({ handleSaveProcessDefinition }: Props) => {
  const dispatch = useAppDispatch();
  const editDefinition = useAppSelector(getEditProcessDefinition);
  const isModalOpen = useAppSelector(getIsProcessDefinitionModalOpen);
  const allDefinitions = useAppSelector(getProcessDefinitions);

  const [isLoading, setIsLoading] = useState<boolean>(false);

  useEffect(() => {
    if (allDefinitions.length === 0) {
      dispatch(fetchProcessDefinitions({ all: true }));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dispatch]);

  const nextProcessOptions = allDefinitions
    .filter((d) => d.id !== editDefinition?.id)
    .map((d) => ({ value: d.id, label: localizedProcessName(d) }));

  const rateBasisOptions: { value: ProcessRateBasis; label: string }[] = [
    { value: "piece", label: t.processDefinitions.rateBasisPiece },
    { value: "weight", label: t.processDefinitions.rateBasisWeight },
    { value: "size", label: t.processDefinitions.rateBasisSize },
  ];

  const modePolicyOptions: { value: ProcessModePolicy; label: string }[] = [
    { value: "both", label: t.processDefinitions.modePolicyBoth },
    { value: "in_house", label: t.processDefinitions.modePolicyInHouse },
    { value: "outsource", label: t.processDefinitions.modePolicyOutsource },
  ];

  const {
    handleSubmit,
    register,
    control,
    reset,
    getValues,
    formState: { errors },
  } = useForm<ProcessDefinitionFormInput>({ defaultValues });

  const isEditing = !!(editDefinition && editDefinition.id);

  const handleOnSubmit: SubmitHandler<ProcessDefinitionFormInput> = async (data) => {
    setIsLoading(true);
    await handleSaveProcessDefinition(data);
    handleCloseFormModal();
    setIsLoading(false);
  };

  const handleCloseFormModal = () => {
    reset(defaultValues);
    dispatch(toggleProcessDefinitionModal(false));
  };

  useEffect(() => {
    if (editDefinition && editDefinition.id) {
      reset({
        slug: editDefinition.slug,
        name: editDefinition.name,
        name_gu: editDefinition.name_gu || "",
        sort_order: editDefinition.sort_order,
        rate_basis: editDefinition.rate_basis,
        is_active: editDefinition.is_active,
        has_order: editDefinition.has_order,
        has_receive: editDefinition.has_receive,
        mode_policy: editDefinition.mode_policy,
        next_process_definition_ids: editDefinition.next_process_definition_ids || [],
        next_process_mandatory: editDefinition.next_process_mandatory || false,
      });
    } else {
      reset(defaultValues);
    }
  }, [editDefinition, isModalOpen]);

  return (
    <Dialog
      open={isModalOpen}
      onOpenChange={(open) => {
        if (!open) handleCloseFormModal();
      }}
    >
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle className="text-xl font-bold text-[#113F67]">
            {t.processDefinitions.formTitle}
          </DialogTitle>
          <DialogDescription>
            {isEditing ? `${t.processDefinitions.editingPrefix} ${editDefinition?.name}` : t.processDefinitions.addNewProcess}
          </DialogDescription>
        </DialogHeader>

        <form onSubmit={handleSubmit(handleOnSubmit)} className="space-y-4 flex-1 flex flex-col sm:block pb-16 sm:pb-0 sm:space-y-5">
          <div className="space-y-2">
            <Label htmlFor="slug" className="text-sm font-semibold text-[#113F67]">
              {t.processDefinitions.slugLabel} <span className="text-red-500">*</span>
            </Label>
            <Input
              id="slug"
              placeholder={t.processDefinitions.slugPlaceholder}
              disabled={isEditing}
              {...register("slug", { required: true, pattern: /^[a-zA-Z0-9_-]+$/ })}
              className={modalInputClass(!!errors.slug)}
            />
            {isEditing ? (
              <p className="text-xs text-gray-500">{t.processDefinitions.slugImmutableHint}</p>
            ) : errors.slug ? (
              <p className="text-sm text-red-500 flex items-center gap-1">
                <AlertCircle className="h-4 w-4" />{t.processDefinitions.slugInvalid}
              </p>
            ) : null}
          </div>

          <div className="space-y-2">
            <Label htmlFor="name" className="text-sm font-semibold text-[#113F67]">
              {t.processDefinitions.nameLabel} <span className="text-red-500">*</span>
            </Label>
            <Input
              id="name"
              placeholder={t.processDefinitions.namePlaceholder}
              {...register("name", { required: true })}
              className={modalInputClass(!!errors.name)}
            />
            {errors.name && (
              <p className="text-sm text-red-500 flex items-center gap-1">
                <AlertCircle className="h-4 w-4" />{t.processDefinitions.nameRequired}
              </p>
            )}
          </div>

          <div className="space-y-2">
            <Label htmlFor="name_gu" className="text-sm font-semibold text-[#113F67]">
              {t.processDefinitions.nameGuLabel}
            </Label>
            <Input
              id="name_gu"
              placeholder={t.processDefinitions.nameGuPlaceholder}
              {...register("name_gu")}
              className={modalInputClass(false)}
            />
          </div>

          <div className="space-y-2">
            <Label htmlFor="rate_basis" className="text-sm font-semibold text-[#113F67]">
              {t.processDefinitions.rateBasisLabel} <span className="text-red-500">*</span>
            </Label>
            <Controller
              name="rate_basis"
              control={control}
              rules={{ validate: (v) => !!v || t.processDefinitions.rateBasisRequired }}
              render={({ field }) => (
                <ReactSelect
                  inputId="rate_basis"
                  styles={modalSelectStyles}
                  options={rateBasisOptions}
                  value={rateBasisOptions.find((o) => o.value === field.value) || null}
                  onChange={(opt: any) => field.onChange(opt?.value || "")}
                  placeholder={t.processDefinitions.rateBasisPlaceholder}
                  isClearable={false}
                />
              )}
            />
            {errors.rate_basis && (
              <p className="text-sm text-red-500 flex items-center gap-1">
                <AlertCircle className="h-4 w-4" />{t.processDefinitions.rateBasisRequired}
              </p>
            )}
          </div>

          <div className="space-y-2">
            <Label htmlFor="sort_order" className="text-sm font-semibold text-[#113F67]">
              {t.processDefinitions.sortOrderLabel}
            </Label>
            <Input
              id="sort_order"
              type="number"
              placeholder={t.processDefinitions.sortOrderPlaceholder}
              {...register("sort_order")}
              className={modalInputClass(false)}
            />
          </div>

          <div className="space-y-3 pt-1">
            <div className="flex items-center gap-2">
              <Controller
                name="is_active"
                control={control}
                render={({ field }) => (
                  <Checkbox id="is_active" checked={field.value} onCheckedChange={field.onChange} />
                )}
              />
              <Label htmlFor="is_active" className="text-sm text-gray-700 cursor-pointer">
                {t.processDefinitions.isActiveLabel}
              </Label>
            </div>
            <div className="flex items-center gap-2">
              <Controller
                name="has_order"
                control={control}
                render={({ field }) => (
                  <Checkbox id="has_order" checked={field.value} onCheckedChange={field.onChange} />
                )}
              />
              <Label htmlFor="has_order" className="text-sm text-gray-700 cursor-pointer">
                {t.processDefinitions.hasOrderLabel}
              </Label>
            </div>
            <div className="flex items-center gap-2">
              <Controller
                name="has_receive"
                control={control}
                render={({ field }) => (
                  <Checkbox id="has_receive" checked={field.value} onCheckedChange={field.onChange} />
                )}
              />
              <Label htmlFor="has_receive" className="text-sm text-gray-700 cursor-pointer">
                {t.processDefinitions.hasReceiveLabel}
              </Label>
            </div>
          </div>

          <div className="space-y-2">
            <Label htmlFor="mode_policy" className="text-sm font-semibold text-[#113F67]">
              {t.processDefinitions.modePolicyLabel} <span className="text-red-500">*</span>
            </Label>
            <Controller
              name="mode_policy"
              control={control}
              rules={{ validate: (v) => !!v || t.processDefinitions.modePolicyRequired }}
              render={({ field }) => (
                <ReactSelect
                  inputId="mode_policy"
                  styles={modalSelectStyles}
                  options={modePolicyOptions}
                  value={modePolicyOptions.find((o) => o.value === field.value) || null}
                  onChange={(opt: any) => field.onChange(opt?.value || "")}
                  placeholder={t.processDefinitions.modePolicyPlaceholder}
                  isClearable={false}
                />
              )}
            />
            {errors.mode_policy && (
              <p className="text-sm text-red-500 flex items-center gap-1">
                <AlertCircle className="h-4 w-4" />{t.processDefinitions.modePolicyRequired}
              </p>
            )}
          </div>

          <div className="space-y-2">
            <Label htmlFor="next_process_definition_ids" className="text-sm font-semibold text-[#113F67]">
              {t.processDefinitions.nextProcessLabel}
            </Label>
            <Controller
              name="next_process_definition_ids"
              control={control}
              rules={{
                validate: (val) =>
                  !getValues("next_process_mandatory") ||
                  (Array.isArray(val) && val.length > 0) ||
                  t.processDefinitions.nextProcessListRequired,
              }}
              render={({ field }) => (
                <ReactSelect
                  inputId="next_process_definition_ids"
                  isMulti
                  styles={modalSelectStyles}
                  options={nextProcessOptions}
                  value={nextProcessOptions.filter((o) => field.value?.includes(o.value))}
                  onChange={(selected: any) => field.onChange(selected ? selected.map((o: any) => o.value) : [])}
                  placeholder={t.processDefinitions.nextProcessPlaceholder}
                />
              )}
            />
            {errors.next_process_definition_ids ? (
              <p className="text-sm text-red-500 flex items-center gap-1">
                <AlertCircle className="h-4 w-4" />{errors.next_process_definition_ids.message}
              </p>
            ) : (
              <p className="text-xs text-gray-500">{t.processDefinitions.nextProcessHint}</p>
            )}
          </div>

          <div className="flex items-center gap-2">
            <Controller
              name="next_process_mandatory"
              control={control}
              render={({ field }) => (
                <Checkbox id="next_process_mandatory" checked={field.value} onCheckedChange={field.onChange} />
              )}
            />
            <Label htmlFor="next_process_mandatory" className="text-sm text-gray-700 cursor-pointer">
              {t.processDefinitions.nextProcessMandatoryLabel}
            </Label>
          </div>

          <div className="fixed bottom-0 left-0 right-0 flex gap-3 p-4 justify-end border-t bg-white z-10 sm:static sm:p-0 sm:pt-2 sm:z-auto sm:border-t sm:bg-transparent">
            <Button
              type="button"
              variant="outline"
              className="h-11 px-5 rounded-lg border-gray-300 text-[#113F67] hover:bg-gray-50 hover:text-[#113F67]"
              onClick={handleCloseFormModal}
              disabled={isLoading}
            >
              {t.processDefinitions.cancelButton}
            </Button>
            <Button
              type="submit"
              className="h-11 px-5 rounded-lg bg-[#113F67] hover:bg-[#0d2f4f] border border-[#113F67]"
              disabled={isLoading}
            >
              {isLoading ? t.processDefinitions.saving : t.processDefinitions.saveButton}
            </Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
};

export default ProcessDefinitionFormModal;
